import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Stack,
  Alert,
  Container,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
  Fade,
  Divider,
  Grid,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom,
  Slide
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ForumIcon from '@mui/icons-material/Forum';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import ShareIcon from '@mui/icons-material/Share';
import PrintIcon from '@mui/icons-material/Print';
import PollIcon from '@mui/icons-material/Poll';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import QRCode from 'react-qr-code';
import { Opinion } from '../../interface/Opinion';
import { Comment } from '../../interface/Comment';
import { getOpinionById, voteOpinion, submitPeerQuestionVotes } from '../../services/OpinionService';
import { getComments, createComment } from '../../services/CommentService';
import { followUser, unfollowUser, getFollowStatus } from '../../services/FollowService';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../auth/LoginModal';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import MainLayout from '../layout/MainLayout';
import UserAvatar from '../common/UserAvatar';

const OpinionDetail: React.FC = () => {
  const { user, logout } = useAuth();
  const currentUserId = user?._id;
  const currentUsername = user?.username;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [opinion, setOpinion] = useState<Opinion | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [voting, setVoting] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState('');
  const [error, setError] = useState('');
  const [shareSuccess, setShareSuccess] = useState(false);
  const [peerAnswers, setPeerAnswers] = useState<number[]>([]);
  const [peerSubmitted, setPeerSubmitted] = useState(false);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loadingFollow, setLoadingFollow] = useState(false);

  useEffect(() => {
    if (id) {
      loadOpinion();
      loadComments();
    }
  }, [id]);

  useEffect(() => {
    if (opinion?.userId && currentUserId && opinion.userId !== currentUserId) {
      loadFollowStatus();
    }
  }, [opinion?.userId, currentUserId]);

  const loadFollowStatus = async () => {
    if (!opinion?.userId) return;
    try {
      const status = await getFollowStatus(opinion.userId);
      setIsFollowing(status.isFollowing);
    } catch (error) {
      console.error('Error loading follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!opinion?.userId || !currentUserId) {
      setLoginModalMessage('Please log in to follow users.');
      setLoginModalOpen(true);
      return;
    }

    try {
      setLoadingFollow(true);
      if (isFollowing) {
        await unfollowUser(opinion.userId);
        setIsFollowing(false);
      } else {
        await followUser(opinion.userId);
        setIsFollowing(true);
      }
    } catch (error: any) {
      console.error('Error following/unfollowing:', error);
      alert(error.response?.data?.error || 'Failed to update follow status');
    } finally {
      setLoadingFollow(false);
    }
  };

  const loadOpinion = async () => {
    if (!id) return;
    try {
      const data = await getOpinionById(id);
      setOpinion(data);
      if (data?.questions && data.questions.length > 0) {
        setPeerAnswers(new Array(data.questions.length).fill(-1));
        setPeerSubmitted(false);
      }
    } catch (error: any) {
      console.error('Error loading opinion:', error);
      if (error.response?.status === 403) {
        setError('This opinion is private');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    if (!id) return;
    try {
      const response = await getComments(id);
      setComments(response.comments);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleVote = async (voteType: 'agree' | 'disagree') => {
    if (!currentUserId || !id) {
      setLoginModalMessage('Please log in to vote on this opinion.');
      setLoginModalOpen(true);
      return;
    }

    try {
      setVoting(true);
      const response = await voteOpinion(id, {
        userId: currentUserId,
        voteType
      });
      setOpinion(response.opinion);
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to vote. Please try again.');
    } finally {
      setVoting(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId || !currentUsername || !id) {
      setLoginModalMessage('Please log in to comment on this opinion.');
      setLoginModalOpen(true);
      return;
    }

    if (!commentContent.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      await createComment(id, {
        content: commentContent.trim(),
        userId: currentUserId,
        username: currentUsername
      });
      setCommentContent('');
      loadComments();
      loadOpinion();
    } catch (error: any) {
      console.error('Error creating comment:', error);
      alert(error.response?.data?.error || 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: opinion?.content.substring(0, 50) + '...' || 'Opinion',
      text: opinion?.content.substring(0, 100) || '',
      url: url
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        setShareSuccess(true);
      } else {
        await navigator.clipboard.writeText(url);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
        } catch (clipboardError) {
          console.error('Failed to copy to clipboard:', clipboardError);
        }
      }
    }
  };

  const renderComments = (commentList: Comment[], level: number = 0) => {
    return commentList.map((comment, index) => (
      <Zoom in timeout={300 + index * 50} key={comment._id}>
        <Box ml={level * 2} mb={1.5}>
          <Card
            elevation={0}
            sx={{
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 3,
              border: '1px solid',
              borderColor: level === 0 ? 'rgba(102, 126, 234, 0.12)' : 'rgba(102, 126, 234, 0.08)',
              background: level === 0 
                ? 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)'
                : 'rgba(255,255,255,0.8)',
              boxShadow: level === 0 
                ? '0 8px 32px rgba(102, 126, 234, 0.1), 0 2px 8px rgba(0,0,0,0.06)'
                : '0 4px 16px rgba(102, 126, 234, 0.08)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: level === 0
                  ? '0 12px 48px rgba(102, 126, 234, 0.18), 0 4px 16px rgba(0,0,0,0.1)'
                  : '0 8px 24px rgba(102, 126, 234, 0.15)',
                borderColor: 'primary.light'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  {comment.username.charAt(0).toUpperCase()}
                </Avatar>
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
                    <Typography 
                      variant="body1" 
                      fontWeight={600} 
                      sx={{ 
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        color: 'text.primary'
                      }}
                    >
                      {comment.username}
                    </Typography>
                    <Chip
                      label={formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        bgcolor: 'grey.100',
                        color: 'text.secondary',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -4,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '4px 0 0 4px'
                      }
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.85,
                        fontSize: { xs: '0.9375rem', sm: '1.0625rem', md: '1.125rem' },
                        color: 'text.primary',
                        whiteSpace: 'pre-wrap',
                        letterSpacing: '0.01em'
                      }}
                    >
                      {comment.content}
                    </Typography>
                  </Box>
                  {comment.replies && comment.replies.length > 0 && (
                    <Box mt={2.5} pt={2.5} borderTop="1px solid" borderColor="divider">
                      <Typography 
                        variant="caption" 
                        color="primary.main" 
                        fontWeight={600} 
                        sx={{ 
                          mb: 2, 
                          display: 'block', 
                          fontSize: '0.8125rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        {comment.replies.length} {comment.replies.length === 1 ? 'Reply' : 'Replies'}
                      </Typography>
                      {renderComments(comment.replies, level + 1)}
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Zoom>
    ));
  };

  if (loading) {
    return (
      <MainLayout hideSidebar={!currentUserId}>
        <Helmet>
          <title>Loading Opinion | OpinLoop</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
        </Box>
      </MainLayout>
    );
  }

  if (!opinion) {
    return (
      <MainLayout hideSidebar={!currentUserId}>
        <Helmet>
          <title>Opinion Not Found | OpinLoop</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            {error || 'Opinion not found'}
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  const totalVotes = (opinion.agreeCount || 0) + (opinion.disagreeCount || 0);
  const agreePercentage = totalVotes > 0
    ? Math.round((opinion.agreeCount / totalVotes) * 100)
    : 0;
  const isTrending = totalVotes > 10;

  const opinionUrl = typeof window !== 'undefined' ? `${window.location.origin}/opinions/${id}` : '';
  const opinionTitle = opinion.content.length > 60 
    ? opinion.content.substring(0, 60) + '...' 
    : opinion.content;
  const opinionDescription = opinion.content.length > 160 
    ? opinion.content.substring(0, 160) + '...' 
    : opinion.content;

  return (
    <MainLayout hideSidebar={!currentUserId}>
      <Helmet>
        <title>{opinionTitle} | OpinLoop</title>
        <meta name="description" content={opinionDescription} />
        <meta name="keywords" content={`${opinion.category || 'opinion'}, ${opinion.targetAudience?.join(', ') || ''}, discussion, feedback, ${opinion.username}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={opinionUrl} />
        <meta property="og:title" content={opinionTitle} />
        <meta property="og:description" content={opinionDescription} />
        <meta property="og:site_name" content="OpinLoop" />
        <meta property="article:author" content={opinion.username} />
        {opinion.category && <meta property="article:section" content={opinion.category} />}
        <meta property="article:published_time" content={opinion.createdAt} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={opinionUrl} />
        <meta property="twitter:title" content={opinionTitle} />
        <meta property="twitter:description" content={opinionDescription} />
        <link rel="canonical" href={opinionUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content={opinion.username} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DiscussionForumPosting",
            "headline": opinionTitle,
            "description": opinionDescription,
            "author": {
              "@type": "Person",
              "name": opinion.username
            },
            "datePublished": opinion.createdAt,
            "dateModified": opinion.updatedAt,
            "url": opinionUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": opinionUrl
            },
            "interactionStatistic": [
              {
                "@type": "InteractionCounter",
                "interactionType": "https://schema.org/AgreeAction",
                "userInteractionCount": opinion.agreeCount
              },
              {
                "@type": "InteractionCounter",
                "interactionType": "https://schema.org/DisagreeAction",
                "userInteractionCount": opinion.disagreeCount
              },
              {
                "@type": "InteractionCounter",
                "interactionType": "https://schema.org/CommentAction",
                "userInteractionCount": comments.length
              }
            ],
            "keywords": `${opinion.category || 'opinion'}, ${opinion.targetAudience?.join(', ') || ''}, discussion, feedback`
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Main Content */}
            <Grid item xs={12} md={8}>
              {/* Opinion Card */}
              <Fade in timeout={600}>
                <Card
                  elevation={0}
                  sx={{
                    mb: 3,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'rgba(102, 126, 234, 0.12)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)',
                    boxShadow: '0 12px 48px rgba(102, 126, 234, 0.12), 0 4px 16px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: '0 16px 64px rgba(102, 126, 234, 0.16), 0 6px 24px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 5,
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                    {/* Author Section */}
                    <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                      <UserAvatar
                        username={opinion.username}
                        size={56}
                        sx={{
                          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.25)',
                          border: '3px solid white'
                        }}
                      />
                      <Box flex={1} minWidth={200}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={1} flexWrap="wrap">
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                              cursor: 'pointer',
                              color: 'text.primary',
                              '&:hover': { 
                                color: 'primary.main',
                                textDecoration: 'underline'
                              },
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => navigate(`/profile/${opinion.userId}`)}
                          >
                            {opinion.username}
                          </Typography>
                          {currentUserId && opinion.userId !== currentUserId && (
                            <Button
                              size="small"
                              variant={isFollowing ? 'outlined' : 'contained'}
                              startIcon={isFollowing ? <PersonRemoveIcon /> : <PersonAddIcon />}
                              onClick={handleFollow}
                              disabled={loadingFollow}
                              sx={{
                                minWidth: 'auto',
                                px: 2,
                                py: 0.75,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 600
                              }}
                            >
                              {isFollowing ? 'Unfollow' : 'Follow'}
                            </Button>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                          <Chip
                            icon={<PersonIcon sx={{ fontSize: 14 }} />}
                            label={formatDistanceToNow(new Date(opinion.createdAt), { addSuffix: true })}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              fontWeight: 500
                            }}
                          />
                          {opinion.category && (
                            <Chip
                              label={opinion.category}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          )}
                          {opinion.isPublic && (
                            <Chip
                              icon={<PublicIcon sx={{ fontSize: 14 }} />}
                              label="Public"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'info.light',
                                color: 'info.dark',
                                fontWeight: 600
                              }}
                            />
                          )}
                          {isTrending && (
                            <Chip
                              icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                              label="Trending"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'warning.light',
                                color: 'warning.dark',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Share">
                          <IconButton
                            onClick={handleShare}
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              '&:hover': { bgcolor: 'primary.main', color: 'white' },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ShareIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="QR Code">
                          <IconButton
                            onClick={() => setQrCodeOpen(true)}
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              '&:hover': { bgcolor: 'primary.main', color: 'white' },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <QrCodeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open printable brochure">
                          <IconButton
                            onClick={() => {
                              if (!id) return;
                              window.open(`/opinions/${id}/brochure`, '_blank');
                            }}
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              '&:hover': { bgcolor: 'primary.main', color: 'white' },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                        {currentUserId === opinion.userId && (
                          <Tooltip title="View Stats">
                            <IconButton
                              onClick={() => navigate(`/opinions/${id}/stats`)}
                              size="small"
                              sx={{
                                bgcolor: 'grey.100',
                                '&:hover': { bgcolor: 'primary.main', color: 'white' },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <PollIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Opinion Content */}
                    <Box
                      sx={{
                        mb: 4,
                        p: { xs: 3, sm: 4, md: 5 },
                        bgcolor: 'grey.50',
                        borderRadius: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.5)'
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.95,
                          fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.25rem' },
                          color: 'text.primary',
                          fontWeight: 400,
                          whiteSpace: 'pre-wrap',
                          letterSpacing: '0.01em'
                        }}
                      >
                        {opinion.content}
                      </Typography>
                    </Box>

                    {/* Vote Stats */}
                    {totalVotes > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Box
                          sx={{
                            height: 12,
                            borderRadius: 6,
                            bgcolor: 'grey.200',
                            position: 'relative',
                            overflow: 'hidden',
                            mb: 2,
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${agreePercentage}%`,
                              background: 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)',
                              borderRadius: 6,
                              transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                              position: 'relative',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                                animation: 'shimmer 2s infinite'
                              }
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ThumbUpIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            <Typography variant="body1" color="success.main" fontWeight={700} sx={{ fontSize: { xs: '0.9375rem', sm: '1.0625rem' } }}>
                              {agreePercentage}% Agree
                            </Typography>
                            <Chip
                              label={opinion.agreeCount}
                              size="small"
                              sx={{
                                height: 24,
                                bgcolor: 'success.light',
                                color: 'success.dark',
                                fontWeight: 700,
                                fontSize: '0.8125rem'
                              }}
                            />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ThumbDownIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            <Typography variant="body1" color="error.main" fontWeight={700} sx={{ fontSize: { xs: '0.9375rem', sm: '1.0625rem' } }}>
                              {100 - agreePercentage}% Disagree
                            </Typography>
                            <Chip
                              label={opinion.disagreeCount}
                              size="small"
                              sx={{
                                height: 24,
                                bgcolor: 'error.light',
                                color: 'error.dark',
                                fontWeight: 700,
                                fontSize: '0.8125rem'
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 3, borderColor: 'divider' }} />

                    {/* Vote Actions */}
                    <Box display="flex" alignItems="center" justifyContent="center" gap={2} flexWrap="wrap">
                      <Button
                        variant={opinion.agreeCount > 0 ? "contained" : "outlined"}
                        startIcon={<ThumbUpIcon />}
                        onClick={() => handleVote('agree')}
                        disabled={voting || !currentUserId}
                        size="large"
                        sx={{
                          minWidth: { xs: 'calc(50% - 8px)', sm: 160 },
                          py: 1.5,
                          borderRadius: 3,
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1.0625rem', md: '1.125rem' },
                          textTransform: 'none',
                          boxShadow: opinion.agreeCount > 0 ? '0 6px 20px rgba(76, 175, 80, 0.35), 0 2px 8px rgba(76, 175, 80, 0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: opinion.agreeCount > 0 ? '0 10px 32px rgba(76, 175, 80, 0.45), 0 4px 12px rgba(76, 175, 80, 0.3)' : '0 6px 20px rgba(0,0,0,0.15)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Agree
                      </Button>
                      <Button
                        variant={opinion.disagreeCount > 0 ? "contained" : "outlined"}
                        startIcon={<ThumbDownIcon />}
                        onClick={() => handleVote('disagree')}
                        disabled={voting || !currentUserId}
                        size="large"
                        sx={{
                          minWidth: { xs: 'calc(50% - 8px)', sm: 160 },
                          py: 1.5,
                          borderRadius: 3,
                          fontWeight: 700,
                          fontSize: { xs: '0.9375rem', sm: '1.0625rem', md: '1.125rem' },
                          textTransform: 'none',
                          boxShadow: opinion.disagreeCount > 0 ? '0 6px 20px rgba(244, 67, 54, 0.35), 0 2px 8px rgba(244, 67, 54, 0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: opinion.disagreeCount > 0 ? '0 10px 32px rgba(244, 67, 54, 0.45), 0 4px 12px rgba(244, 67, 54, 0.3)' : '0 6px 20px rgba(0,0,0,0.15)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Disagree
                      </Button>
                    </Box>

                    {/* Peer Questions */}
                    {opinion.questions && opinion.questions.length > 0 && (
                      <Box
                        sx={{
                          mt: 4,
                          p: { xs: 2.5, sm: 3.5 },
                          borderRadius: 3,
                          bgcolor: 'grey.50',
                          border: '2px dashed',
                          borderColor: 'primary.light',
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
                          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.08), inset 0 1px 0 rgba(255,255,255,0.5)'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.25rem' } }}>
                              Quick Peer Questions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                              Help the author by sharing your perspective
                            </Typography>
                          </Box>
                          {peerSubmitted && (
                            <Chip
                              icon={<FavoriteIcon />}
                              label="Thanks for sharing!"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>

                        <Stack spacing={2.5}>
                          {opinion.questions.map((q, qIndex) => (
                            <Box key={qIndex}>
                            <Typography
                              variant="subtitle2"
                              sx={{ 
                                fontWeight: 600, 
                                mb: 1.5,
                                fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                                color: 'text.primary'
                              }}
                            >
                              Q{qIndex + 1}. {q.questionText}
                            </Typography>
                              <Box display="flex" flexWrap="wrap" gap={1.5}>
                                {q.options.map((opt, optIndex) => {
                                  const selected = peerAnswers[qIndex] === optIndex;
                                  return (
                                    <Chip
                                      key={optIndex}
                                      label={opt.text}
                                      clickable={!!currentUserId && !peerSubmitted}
                                      onClick={() => {
                                        if (!currentUserId) {
                                          setLoginModalMessage('Please log in to respond to these questions.');
                                          setLoginModalOpen(true);
                                          return;
                                        }
                                        if (peerSubmitted) return;
                                        const next = [...peerAnswers];
                                        next[qIndex] = optIndex;
                                        setPeerAnswers(next);
                                        setPeerSubmitted(false);
                                      }}
                                      sx={{
                                        borderRadius: 3,
                                        fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                                        px: 2.5,
                                        py: 1.25,
                                        height: 'auto',
                                        bgcolor: selected ? 'primary.main' : 'white',
                                        color: selected ? 'white' : 'text.primary',
                                        border: '2px solid',
                                        borderColor: selected ? 'primary.main' : 'rgba(102, 126, 234, 0.2)',
                                        fontWeight: selected ? 600 : 500,
                                        boxShadow: selected 
                                          ? '0 6px 20px rgba(102, 126, 234, 0.35), 0 2px 8px rgba(102, 126, 234, 0.2)' 
                                          : '0 2px 8px rgba(0,0,0,0.08)',
                                        '&:hover': {
                                          bgcolor: selected ? 'primary.dark' : 'grey.100',
                                          transform: 'translateY(-3px)',
                                          boxShadow: selected 
                                            ? '0 10px 32px rgba(102, 126, 234, 0.45), 0 4px 12px rgba(102, 126, 234, 0.3)' 
                                            : '0 4px 16px rgba(0,0,0,0.12)',
                                          borderColor: selected ? 'primary.dark' : 'primary.light'
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                        </Stack>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                          <Button
                            variant="contained"
                            size="medium"
                            disabled={
                              !currentUserId ||
                              peerAnswers.length === 0 ||
                              peerAnswers.every((v) => v === -1)
                            }
                            onClick={async () => {
                              if (!currentUserId) {
                                setLoginModalMessage('Please log in to respond to these questions.');
                                setLoginModalOpen(true);
                                return;
                              }
                              if (!id) return;
                              try {
                                const result = await submitPeerQuestionVotes(id, peerAnswers);
                                setOpinion(result.opinion);
                                setPeerAnswers(result.userSelections || peerAnswers);
                                setPeerSubmitted(true);
                              } catch (error: any) {
                                console.error('Error submitting peer question votes:', error);
                                alert(error?.response?.data?.error || 'Failed to submit responses. Please try again.');
                              }
                            }}
                            sx={{
                              px: 3,
                              py: 1.25,
                              borderRadius: 3,
                              fontWeight: 700,
                              textTransform: 'none',
                              fontSize: '0.9375rem'
                            }}
                          >
                            Submit Responses
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Fade>

              {/* Comment Form */}
              <Fade in timeout={800}>
                <Card
                  elevation={0}
                  sx={{
                    mb: 3,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'rgba(102, 126, 234, 0.12)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)',
                    boxShadow: '0 12px 48px rgba(102, 126, 234, 0.12), 0 4px 16px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: '0 16px 64px rgba(102, 126, 234, 0.16), 0 6px 24px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                      <CommentIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />
                      <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.5rem' } }}>
                        Join the Discussion
                      </Typography>
                    </Box>

                    {currentUserId ? (
                      <form onSubmit={handleSubmitComment}>
                        <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                          <UserAvatar
                            profileIcon={user?.profilePicture}
                            username={currentUsername}
                            size={40}
                            sx={{
                              display: { xs: 'none', sm: 'flex' },
                              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
                            }}
                          />
                          <TextField
                            fullWidth
                            multiline
                            rows={isMobile ? 3 : 4}
                            value={commentContent}
                            onChange={(e) => setCommentContent(e.target.value)}
                            placeholder="Share your thoughts..."
                            variant="outlined"
                            disabled={submittingComment}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                  borderWidth: 2
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main',
                                  borderWidth: 2,
                                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                                }
                              }
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={submittingComment || !commentContent.trim()}
                            startIcon={submittingComment ? <CircularProgress size={16} /> : <SendIcon />}
                            sx={{
                              px: 3.5,
                              py: 1.5,
                              borderRadius: 3,
                              fontWeight: 700,
                              textTransform: 'none',
                              fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.0625rem' },
                              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.35), 0 2px 8px rgba(102, 126, 234, 0.2)',
                              '&:hover': {
                                boxShadow: '0 10px 32px rgba(102, 126, 234, 0.45), 0 4px 12px rgba(102, 126, 234, 0.3)',
                                transform: 'translateY(-3px)'
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                          >
                            {submittingComment ? 'Posting...' : 'Post Comment'}
                          </Button>
                        </Box>
                      </form>
                    ) : (
                      <Alert
                        severity="info"
                        sx={{
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            fontSize: 24
                          }
                        }}
                        action={
                          <Button color="inherit" size="small" onClick={() => navigate('/login')} sx={{ fontWeight: 600 }}>
                            Login
                          </Button>
                        }
                      >
                        Please log in to join the discussion.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Fade>

              {/* Comments Section */}
              <Fade in timeout={1000}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                    <CommentIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.5rem' } }}>
                      Comments
                    </Typography>
                    <Chip
                      label={comments.length}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        height: 32,
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        fontWeight: 700
                      }}
                    />
                  </Box>

                  {comments.length === 0 ? (
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'rgba(102, 126, 234, 0.12)',
                        background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.08), 0 2px 8px rgba(0,0,0,0.04)'
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <CommentIcon sx={{ fontSize: { xs: 56, sm: 64 }, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                          No comments yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                          Be the first to share your thoughts!
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Stack spacing={2}>
                      {renderComments(comments)}
                    </Stack>
                  )}
                </Box>
              </Fade>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <Fade in timeout={1000}>
                <Card
                  elevation={0}
                  sx={{
                    position: 'sticky',
                    top: 20,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'rgba(102, 126, 234, 0.12)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,250,252,1) 100%)',
                    boxShadow: '0 12px 48px rgba(102, 126, 234, 0.12), 0 4px 16px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      boxShadow: '0 16px 64px rgba(102, 126, 234, 0.16), 0 6px 24px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      p: 3,
                      color: 'white',
                      textAlign: 'center'
                    }}
                  >
                    <ForumIcon sx={{ fontSize: { xs: 28, sm: 32 }, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                      Opinion Stats
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      {opinion.category && (
                        <>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                              Category
                            </Typography>
                            <Chip
                              label={opinion.category}
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                height: 32
                              }}
                            />
                          </Box>
                          <Divider />
                        </>
                      )}
                      <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Total Votes
                          </Typography>
                          <Typography variant="h5" fontWeight={800} color="primary.main" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                            {totalVotes}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={totalVotes > 0 ? (opinion.agreeCount / totalVotes) * 100 : 0}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                            }
                          }}
                        />
                      </Box>
                      <Divider />
                      <Box>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'success.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ThumbUpIcon sx={{ color: 'success.main', fontSize: 24 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                              Agree
                            </Typography>
                            <Typography variant="h5" fontWeight={800} color="success.main" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                              {opinion.agreeCount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'error.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ThumbDownIcon sx={{ color: 'error.main', fontSize: 24 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                              Disagree
                            </Typography>
                            <Typography variant="h5" fontWeight={800} color="error.main" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                              {opinion.disagreeCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Divider />
                      <Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CommentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                              Comments
                            </Typography>
                            <Typography variant="h5" fontWeight={800} color="primary.main" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                              {comments.length}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {opinion.isPublic && (
                        <>
                          <Divider />
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: 'info.light',
                              borderRadius: 2,
                              textAlign: 'center'
                            }}
                          >
                            <PublicIcon sx={{ color: 'info.dark', fontSize: 32, mb: 1 }} />
                            <Typography variant="body2" color="info.dark" fontWeight={700} sx={{ mb: 0.5 }}>
                              Public Opinion
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Visible to everyone
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        message={loginModalMessage}
      />

      {/* Share Success Snackbar */}
      <Snackbar
        open={shareSuccess}
        autoHideDuration={3000}
        onClose={() => setShareSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setShareSuccess(false)}
          severity="success"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
          icon={<ContentCopyIcon />}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>

      {/* QR Code Dialog */}
      <Dialog
        open={qrCodeOpen}
        onClose={() => setQrCodeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
            {/* Logo */}
            <Box
              component="img"
              src="/images/logo.png"
              alt="OpinLoop"
              sx={{
                height: { xs: 40, sm: 48 },
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                borderRadius: 2
              }}
            />
            {/* Title */}
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <QrCodeIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                Scan to Share
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'white',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
            }}
          >
            <QRCode
              value={opinionUrl}
              size={isMobile ? 200 : 256}
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
              viewBox="0 0 256 256"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2, wordBreak: 'break-all', px: 2, fontSize: '0.875rem' }}>
            {opinionUrl}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(opinionUrl);
                setShareSuccess(true);
                setTimeout(() => setShareSuccess(false), 3000);
              } catch (error) {
                console.error('Failed to copy URL:', error);
              }
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Copy Link
          </Button>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => setQrCodeOpen(false)} 
            variant="contained" 
            color="primary"
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </MainLayout>
  );
};

export default OpinionDetail;
